import type {
    ConfigTarget,
    SettingsRegistryEntry,
    SettingsSnapshot,
} from "@magic-md/engine-client/config";
import { useT } from "@magic-md/i18n/react";
import { Loader2, Search } from "lucide-react";
import { useMemo } from "react";
import { cn } from "../../../lib/utils";
import {
    PluginList,
    type PluginListItemModel,
} from "../../PluginList";
import {
    ExtensionList,
    type ExtensionListItemModel,
    type ExtensionSourceFilter,
} from "../../ExtensionList";
import { PluginSourceBadge } from "../../PluginSourceBadge";
import {
    SidecarList,
    type SidecarListItemModel,
    type SidecarSourceFilter,
} from "../../SidecarList";
import type { PluginSourceFilter } from "../../../plugins/plugin-source-metadata";
import type { SettingsSection } from "../../../store/settings-store";
import { buildEntryListGroups } from "./builders";
import { EFFECT_LABELS } from "./constants";
import { summarizeSettingValue } from "./format-utils";
import { EmptyState, MiniBadge } from "./LayoutComponents";
import {
    settingsEntryTitle,
    type SettingsTextResolver,
    useSettingsTextResolver,
} from "./localization";
import { getCurrentLayerValue, isWritable } from "./resolvers";
import type { SettingGroup } from "./types";

export function EntrySettingsPanel({
    title,
    section,
    entries,
    selectedKey,
    onSelect,
    searchQuery,
    onSearchChange,
    target,
    snapshot,
    drafts,
    detail,
}: {
    title: string;
    section: SettingsSection;
    entries: SettingsRegistryEntry[];
    selectedKey: string | null;
    onSelect: (key: string) => void;
    searchQuery: string;
    onSearchChange: (query: string) => void;
    target: ConfigTarget;
    snapshot: SettingsSnapshot | null;
    drafts: Record<string, unknown>;
    detail: React.ReactNode;
}) {
    const t = useT("editor");
    const resolveText = useSettingsTextResolver();
    const entryGroups = useMemo(
        () => buildEntryListGroups(section, entries),
        [entries, section],
    );

    return (
        <>
            <div className="flex w-[320px] shrink-0 flex-col border-r border-border">
                <ListPaneHeader
                    title={title}
                    searchQuery={searchQuery}
                    onSearchChange={onSearchChange}
                    placeholder={t("settings.search.placeholder")}
                />
                <div className="flex min-h-0 flex-1 flex-col overflow-auto">
                    {entryGroups.length === 0 ? (
                        <EmptyState message={t("settings.empty.noMatches")} />
                    ) : entryGroups.map((group) => (
                        <div
                            key={group.id}
                            className="border-b border-border/60 last:border-b-0"
                        >
                            {entryGroups.length > 1 ? (
                                <div className="sticky top-0 z-10 border-b border-border/60 bg-muted/30 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground backdrop-blur">
                                    {resolveText(group.title)}
                                </div>
                            ) : null}
                            {group.entries.map((entry) => (
                                <EntryListRow
                                    key={entry.key}
                                    entry={entry}
                                    selected={selectedKey === entry.key}
                                    target={target}
                                    snapshot={snapshot}
                                    draftValue={drafts[entry.key]}
                                    onSelect={() => onSelect(entry.key)}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            </div>
            <div className="min-w-0 flex-1 overflow-auto">
                {detail ? (
                    <div className="min-h-full px-4 py-3 sm:px-5">{detail}</div>
                ) : (
                    <EmptyState message={t("settings.empty.selectSetting")} />
                )}
            </div>
        </>
    );
}

export function GroupSettingsPanel({
    title,
    groups,
    selectedId,
    onSelect,
    searchQuery,
    onSearchChange,
    target,
    snapshot,
    drafts,
    savingKeys,
    onToggleEnabled,
    detail,
    variant = "settings",
    sourceFilter,
    onSourceFilterChange,
    sidecarSourceFilter,
    onSidecarSourceFilterChange,
    extensionSourceFilter,
    onExtensionSourceFilterChange,
}: {
    title: string;
    groups: SettingGroup[];
    selectedId: string | null;
    onSelect: (id: string) => void;
    searchQuery: string;
    onSearchChange: (query: string) => void;
    target: ConfigTarget;
    snapshot: SettingsSnapshot | null;
    drafts: Record<string, unknown>;
    savingKeys: string[];
    onToggleEnabled: (
        entry: SettingsRegistryEntry,
        value: boolean,
    ) => Promise<void>;
    detail: React.ReactNode;
    variant?: "settings" | "plugin" | "sidecar" | "extension";
    sourceFilter?: PluginSourceFilter;
    onSourceFilterChange?: (filter: PluginSourceFilter) => void;
    sidecarSourceFilter?: SidecarSourceFilter;
    onSidecarSourceFilterChange?: (filter: SidecarSourceFilter) => void;
    extensionSourceFilter?: ExtensionSourceFilter;
    onExtensionSourceFilterChange?: (filter: ExtensionSourceFilter) => void;
}) {
    const t = useT("editor");
    const resolveText = useSettingsTextResolver();
    const pluginItems = useMemo<PluginListItemModel[]>(
        () =>
            variant === "plugin"
                ? groups.map((group) =>
                    buildPluginListItem({
                        group,
                        resolveText,
                        target,
                        snapshot,
                        drafts,
                        savingKeys,
                        onToggleEnabled,
                    }),
                )
                : [],
        [
            drafts,
            groups,
            onToggleEnabled,
            resolveText,
            savingKeys,
            snapshot,
            target,
            variant,
        ],
    );
    const sidecarItems = useMemo<SidecarListItemModel[]>(
        () =>
            variant === "sidecar"
                ? groups.map((group) =>
                    buildSidecarListItem({
                        group,
                        resolveText,
                        target,
                        snapshot,
                        drafts,
                        savingKeys,
                        onToggleEnabled,
                    }),
                )
                : [],
        [
            drafts,
            groups,
            onToggleEnabled,
            resolveText,
            savingKeys,
            snapshot,
            target,
            variant,
        ],
    );
    const extensionItems = useMemo<ExtensionListItemModel[]>(
        () =>
            variant === "extension"
                ? groups.map((group) =>
                    buildExtensionListItem({
                        group,
                        resolveText,
                        target,
                        snapshot,
                        drafts,
                        savingKeys,
                        onToggleEnabled,
                    }),
                )
                : [],
        [
            drafts,
            groups,
            onToggleEnabled,
            resolveText,
            savingKeys,
            snapshot,
            target,
            variant,
        ],
    );

    return (
        <>
            <div className="flex w-[320px] shrink-0 flex-col border-r border-border">
                {variant === "plugin" ? (
                    <PluginList
                        items={pluginItems}
                        selectedId={selectedId}
                        onSelect={onSelect}
                        title={title}
                        searchQuery={searchQuery}
                        onSearchChange={onSearchChange}
                        sourceFilter={sourceFilter}
                        onSourceFilterChange={onSourceFilterChange}
                        searchPlaceholder={t("settings.search.namePlaceholder")}
                        emptyMessage={t("settings.empty.noMatches")}
                        noMatchesMessage={t("settings.empty.noMatches")}
                    />
                ) : variant === "sidecar" ? (
                    <SidecarList
                        items={sidecarItems}
                        selectedId={selectedId}
                        onSelect={onSelect}
                        title={title}
                        searchQuery={searchQuery}
                        onSearchChange={onSearchChange}
                        sourceFilter={sidecarSourceFilter}
                        onSourceFilterChange={onSidecarSourceFilterChange}
                        searchPlaceholder={t("settings.search.namePlaceholder")}
                        emptyMessage={t("settings.empty.noMatches")}
                        noMatchesMessage={t("settings.empty.noMatches")}
                    />
                ) : variant === "extension" ? (
                    <ExtensionList
                        items={extensionItems}
                        selectedId={selectedId}
                        onSelect={onSelect}
                        title={title}
                        searchQuery={searchQuery}
                        onSearchChange={onSearchChange}
                        sourceFilter={extensionSourceFilter}
                        onSourceFilterChange={onExtensionSourceFilterChange}
                        searchPlaceholder={t("settings.search.namePlaceholder")}
                        emptyMessage={t("settings.empty.noMatches")}
                        noMatchesMessage={t("settings.empty.noMatches")}
                    />
                ) : (
                    <>
                        <ListPaneHeader
                            title={title}
                            searchQuery={searchQuery}
                            onSearchChange={onSearchChange}
                            placeholder={t("settings.search.namePlaceholder")}
                        />
                        <div className="flex min-h-0 flex-1 flex-col overflow-auto">
                            {groups.length === 0 ? (
                                <EmptyState message={t("settings.empty.noMatches")} />
                            ) : groups.map((group) => (
                                <GroupListRow
                                    key={group.id}
                                    group={group}
                                    selected={selectedId === group.id}
                                    target={target}
                                    snapshot={snapshot}
                                    drafts={drafts}
                                    savingKeys={savingKeys}
                                    onSelect={() => onSelect(group.id)}
                                    onToggleEnabled={onToggleEnabled}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
            <div className="min-w-0 flex-1 overflow-auto">
                {detail ? (
                    <div className="min-h-full px-4 py-3 sm:px-5">{detail}</div>
                ) : (
                    <EmptyState message={t("settings.empty.selectEntry")} />
                )}
            </div>
        </>
    );
}

function buildExtensionListItem({
    group,
    resolveText,
    target,
    snapshot,
    drafts,
    savingKeys,
    onToggleEnabled,
}: {
    group: SettingGroup;
    resolveText: SettingsTextResolver;
    target: ConfigTarget;
    snapshot: SettingsSnapshot | null;
    drafts: Record<string, unknown>;
    savingKeys: string[];
    onToggleEnabled: (
        entry: SettingsRegistryEntry,
        value: boolean,
    ) => Promise<void>;
}): ExtensionListItemModel {
    const enabledEntry = group.enabledEntry;
    const enabledMeta = enabledEntry
        ? snapshot?.meta.entries[enabledEntry.key]
        : undefined;
    const enabledValue = enabledEntry
        ? getCurrentLayerValue(
            enabledEntry,
            target,
            snapshot,
            drafts[enabledEntry.key],
        ) !== false
        : true;
    const saving = enabledEntry ? savingKeys.includes(enabledEntry.key) : false;
    const enabledWritable = enabledEntry
        ? isWritable(enabledEntry, target, snapshot?.capabilities, enabledMeta)
        : false;

    return {
        id: group.id,
        title: resolveText(group.title, group.id),
        icon: group.icon,
        description: group.description
            ? resolveText(group.description, group.description)
            : resolveText(group.summary, group.summary),
        category: group.category,
        version: group.version,
        runtimeType: group.extensionRuntimeType,
        source: group.extensionSource,
        readOnlyLabel:
            !enabledWritable && enabledMeta?.readOnlyReason
                ? resolveText(enabledMeta.readOnlyReason, enabledMeta.readOnlyReason)
                : undefined,
        searchText: [
            group.id,
            resolveText(group.summary, group.summary),
            ...group.entries.map((entry) => entry.key),
        ],
        toggle: {
            checked: enabledValue,
            disabled: !enabledEntry || saving || !enabledWritable,
            loading: saving,
            onChange: (value) => {
                if (!enabledEntry) return;
                void onToggleEnabled(enabledEntry, value);
            },
        },
    };
}

function buildPluginListItem({
    group,
    resolveText,
    target,
    snapshot,
    drafts,
    savingKeys,
    onToggleEnabled,
}: {
    group: SettingGroup;
    resolveText: SettingsTextResolver;
    target: ConfigTarget;
    snapshot: SettingsSnapshot | null;
    drafts: Record<string, unknown>;
    savingKeys: string[];
    onToggleEnabled: (
        entry: SettingsRegistryEntry,
        value: boolean,
    ) => Promise<void>;
}): PluginListItemModel {
    const enabledEntry = group.enabledEntry;
    const enabledMeta = enabledEntry
        ? snapshot?.meta.entries[enabledEntry.key]
        : undefined;
    const enabledValue = enabledEntry
        ? getCurrentLayerValue(
            enabledEntry,
            target,
            snapshot,
            drafts[enabledEntry.key],
        ) !== false
        : true;
    const saving = enabledEntry ? savingKeys.includes(enabledEntry.key) : false;
    const enabledWritable = enabledEntry
        ? isWritable(enabledEntry, target, snapshot?.capabilities, enabledMeta)
        : false;

    return {
        id: group.id,
        title: resolveText(group.title, group.id),
        icon: group.icon,
        description: group.description
            ? resolveText(group.description, group.description)
            : resolveText(group.summary, group.summary),
        version: group.version,
        actionCount: group.actionCount,
        source: group.source,
        status: "ready",
        readOnlyLabel:
            !enabledWritable && enabledMeta?.readOnlyReason
                ? resolveText(enabledMeta.readOnlyReason, enabledMeta.readOnlyReason)
                : undefined,
        searchText: [
            group.id,
            resolveText(group.summary, group.summary),
            ...group.entries.map((entry) => entry.key),
        ],
        toggle: {
            checked: enabledValue,
            disabled: !enabledEntry || saving || !enabledWritable,
            loading: saving,
            onChange: (value) => {
                if (!enabledEntry) return;
                void onToggleEnabled(enabledEntry, value);
            },
        },
    };
}

function buildSidecarListItem({
    group,
    resolveText,
    target,
    snapshot,
    drafts,
    savingKeys,
    onToggleEnabled,
}: {
    group: SettingGroup;
    resolveText: SettingsTextResolver;
    target: ConfigTarget;
    snapshot: SettingsSnapshot | null;
    drafts: Record<string, unknown>;
    savingKeys: string[];
    onToggleEnabled: (
        entry: SettingsRegistryEntry,
        value: boolean,
    ) => Promise<void>;
}): SidecarListItemModel {
    const enabledEntry = group.enabledEntry;
    const enabledMeta = enabledEntry
        ? snapshot?.meta.entries[enabledEntry.key]
        : undefined;
    const enabledValue = enabledEntry
        ? getCurrentLayerValue(
            enabledEntry,
            target,
            snapshot,
            drafts[enabledEntry.key],
        ) !== false
        : true;
    const saving = enabledEntry ? savingKeys.includes(enabledEntry.key) : false;
    const enabledWritable = enabledEntry
        ? isWritable(enabledEntry, target, snapshot?.capabilities, enabledMeta)
        : false;
    const readOnlyLabel =
        !enabledWritable && enabledMeta?.readOnlyReason
            ? resolveText(enabledMeta.readOnlyReason, enabledMeta.readOnlyReason)
            : undefined;

    return {
        id: group.id,
        title: resolveText(group.title, group.id),
        icon: group.icon,
        description: group.description
            ? resolveText(group.description, group.description)
            : resolveText(group.summary, group.summary),
        alive: group.alive,
        sourceKind: group.sidecarSourceKind,
        transportType: group.sidecarTransportType,
        readOnlyLabel,
        searchText: [
            group.id,
            resolveText(group.summary, group.summary),
            ...group.entries.map((entry) => entry.key),
        ],
        toggle: enabledEntry
            ? {
                checked: enabledValue,
                disabled: saving || !enabledWritable,
                loading: saving,
                onChange: (value) => {
                    void onToggleEnabled(enabledEntry, value);
                },
            }
            : undefined,
    };
}

function ListPaneHeader({
    title,
    searchQuery,
    onSearchChange,
    placeholder,
}: {
    title: string;
    searchQuery: string;
    onSearchChange: (query: string) => void;
    placeholder: string;
}) {
    const resolveText = useSettingsTextResolver();
    return (
        <div className="border-b border-border px-3 py-2.5">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {resolveText(title)}
            </div>
            <div className="relative">
                <Search
                    size={14}
                    className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                    type="text"
                    value={searchQuery}
                    onChange={(event) => onSearchChange(event.target.value)}
                    placeholder={resolveText(placeholder)}
                    className="h-8 w-full rounded-md border border-border bg-background pl-8 pr-3 text-sm outline-none transition-colors focus:border-primary"
                />
            </div>
        </div>
    );
}

function EntryListRow({
    entry,
    selected,
    target,
    snapshot,
    draftValue,
    onSelect,
}: {
    entry: SettingsRegistryEntry;
    selected: boolean;
    target: ConfigTarget;
    snapshot: SettingsSnapshot | null;
    draftValue: unknown;
    onSelect: () => void;
}) {
    const resolveText = useSettingsTextResolver();
    const meta = snapshot?.meta.entries[entry.key];
    const currentLayerValue = getCurrentLayerValue(
        entry,
        target,
        snapshot,
        draftValue,
    );
    const finalValue = snapshot?.view.values[entry.key];
    const valueSummary = summarizeSettingValue(
        entry.key,
        currentLayerValue ?? finalValue,
        resolveText,
    );

    return (
        <button
            type="button"
            onClick={onSelect}
            className={cn(
                "w-full border-b border-border/70 px-3 py-2 text-left transition-colors",
                selected ? "bg-primary/5" : "hover:bg-muted/30",
            )}
        >
            <div className="flex min-w-0 items-start justify-between gap-3">
                <span className="min-w-0 truncate text-sm text-foreground">
                    {settingsEntryTitle(entry, resolveText)}
                </span>
                {meta?.effect ? (
                    <span className="shrink-0">
                        <MiniBadge>{resolveText(EFFECT_LABELS[meta.effect])}</MiniBadge>
                    </span>
                ) : null}
            </div>
            <div className="mt-1 flex min-w-0 items-start justify-between gap-3">
                <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-muted-foreground">
                    {entry.key}
                </span>
                <span
                    className="max-w-[112px] shrink-0 truncate text-xs text-muted-foreground"
                    title={valueSummary}
                >
                    {valueSummary}
                </span>
            </div>
        </button>
    );
}

function GroupListRow({
    group,
    selected,
    target,
    snapshot,
    drafts,
    savingKeys,
    onSelect,
    onToggleEnabled,
}: {
    group: SettingGroup;
    selected: boolean;
    target: ConfigTarget;
    snapshot: SettingsSnapshot | null;
    drafts: Record<string, unknown>;
    savingKeys: string[];
    onSelect: () => void;
    onToggleEnabled: (
        entry: SettingsRegistryEntry,
        value: boolean,
    ) => Promise<void>;
}) {
    const resolveText = useSettingsTextResolver();
    const enabledEntry = group.enabledEntry;
    const enabledMeta = enabledEntry
        ? snapshot?.meta.entries[enabledEntry.key]
        : undefined;
    const enabledValue = enabledEntry
        ? getCurrentLayerValue(
            enabledEntry,
            target,
            snapshot,
            drafts[enabledEntry.key],
        ) !== false
        : true;
    const saving = enabledEntry ? savingKeys.includes(enabledEntry.key) : false;
    const enabledWritable = enabledEntry
        ? isWritable(enabledEntry, target, snapshot?.capabilities, enabledMeta)
        : false;

    return (
        <button
            type="button"
            onClick={onSelect}
            className={cn(
                "flex w-full items-start gap-3 border-b border-border/70 px-3 py-1.5 text-left transition-colors",
                selected ? "bg-primary/5" : "hover:bg-muted/30",
            )}
        >
            <label
                className="mt-0.5 flex items-center"
                onClick={(event) => event.stopPropagation()}
            >
                <input
                    type="checkbox"
                    checked={enabledValue}
                    disabled={!enabledEntry || saving || !enabledWritable}
                    onChange={(event) => {
                        if (!enabledEntry) return;
                        void onToggleEnabled(enabledEntry, event.target.checked);
                    }}
                />
            </label>
            <span className="mt-0.5 text-base leading-none">{group.icon}</span>
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                        <span className="truncate text-sm text-foreground">
                            {resolveText(group.title, group.id)}
                        </span>
                        {!enabledWritable && enabledMeta?.readOnlyReason ? (
                            <MiniBadge>
                                {resolveText(enabledMeta.readOnlyReason, enabledMeta.readOnlyReason)}
                            </MiniBadge>
                        ) : null}
                        {saving ? (
                            <Loader2 size={12} className="animate-spin text-muted-foreground" />
                        ) : null}
                    </div>
                    {group.source ? (
                        <PluginSourceBadge
                            source={group.source}
                            compact
                            className="ml-auto"
                        />
                    ) : null}
                </div>
                <div className="mt-1 truncate text-xs text-muted-foreground">
                    {resolveText(group.summary)}
                </div>
            </div>
        </button>
    );
}
